import React, { useContext, useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { useListingRedirect } from '~hooks/useListingRedirect';
import cx from 'classnames';

import { UserContext } from '~contexts/user';
import { LayoutContext } from '~contexts/layout';

import AccountMenu from './account-menu';
import Conversations from './conversations';
import MainMenu from './main-menu';
import SignIn from './sign-in';
import Finalize from './finalize';
import AddOffer from './add-offer';
import AuthorizedMenu from './authorized-menu';
// import TopCTABar from './top-cta-bar';
import TopCTABar from '~components/top-bar';
import InstallPWAButton from './install-pwa-button';
import Stars from '~components/stars';
import { SkeletonSimple } from '~components/skeleton';
import { hasWishOrOperations } from '~helpers/common';

import './style.scss';
import DesktopMenu from './desktop-menu';

const Header = ({isCityPage=false}) => {
  const websiteTitle = useSelector((state) => state.domainConfig.websiteTitle);
  const location = useLocation();
  // Initialize to false so the first client render matches the server (which has no
  // localStorage). Reading localStorage here would diverge from the SSR markup and break
  // hydration. The stored value is applied after mount in the effect below.
  const [topBarHidden, setTopBarHidden] = useState(false);

  useEffect(() => {
    if (window.localStorage.getItem('topBarHidden') === 'true') {
      setTopBarHidden(true);
    }
  }, []);

  const handleTopBarClose = () => {
    setTopBarHidden(true);
    window.localStorage.setItem('topBarHidden', 'true');
  };

  const { user: authUser, isAuthenticated, isLoading: isLoadingAuth } = useAuth0();
  const { user, isLoading: isLoadingBackendUser, isLoaded } = useContext(UserContext);
  const { layout } = useContext(LayoutContext);

  const needsFinalize =
    isAuthenticated &&
    !isLoadingBackendUser &&
    isLoaded &&
    !user?.personId &&
    location.pathname.indexOf('/finalize/') < 0;

  const hasLocation = hasWishOrOperations(user);
  const hasPublishedWish = !!user?.wish?.address?.place_id && user?.wish?.published;

  const finalizeUrl =
    needsFinalize && authUser?.attributes?.identities?.length > 0 ? '/finalize/external' : '/finalize/email';

  const listingRedirect = useListingRedirect();

  // Track mounting to prevent hydration mismatches
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Determine if authentication state or user context is loading
  const isAuthLoading = isMounted && (isLoadingAuth || (isAuthenticated && (isLoadingBackendUser || !isLoaded)));

  // Initial render of SignIn
  const [showSignIn, setShowSignIn] = useState(true);

  // Handle auth state changes after initial render
  useEffect(() => {
    if (isAuthenticated && user?.personId && hasLocation) {
      setShowSignIn(false);
    }
  }, [isAuthenticated, user?.personId, hasLocation]);

  return (
    <>
      <header
        className={cx('c-header', {
          ['c-header--no-mobile']: layout.type === 'no-header-on-mobile',
          ['c-header--with-top-cta-bar']: !isAuthenticated && layout.displayTopCTABar && !topBarHidden,
          ['c-header--logo-to-bottom']: !isAuthenticated && layout.logoToBottom,
        })}
      >
        {/* commented as per reuirement */}
        {!isAuthenticated && layout.displayTopCTABar && !topBarHidden ? (
          <TopCTABar onClose={handleTopBarClose} />
        ) : null}
        
        <div className="c-header__inner l-container">
          {/* Mobile Menu (Left column on mobile, hidden on desktop) */}
          <div className="c-header__left-container c-header__mobile-menu">
            {isAuthLoading ? (
              <SkeletonSimple className="c-header__left-skeleton" />
            ) : (
              <>
                <div className={cx("c-header__left-group", { "c-header__left-group--hidden": isAuthenticated })}>
                  <MainMenu />
                </div>
                <div className={cx("c-header__left-group", { "c-header__left-group--hidden": !isAuthenticated || !user?.personId })}>
                  <InstallPWAButton />
                </div>
              </>
            )}
          </div>

          {/* Logo (Left column on desktop, Center column on mobile) */}
          <div className="c-header-website">
            <Link to={listingRedirect}>
              <img alt={websiteTitle.toLowerCase()} src="/images/logo.svg" width="64" height="64" />
            </Link>
          </div>

          {/* Navigation / Desktop Menu (Center column on desktop, hidden on mobile) */}
          <div className="c-header__desktop-menu-container">
            {isAuthLoading ? (
              <SkeletonSimple className="c-header__desktop-menu-skeleton" />
            ) : (
              <div className={cx("c-header__desktop-menu-wrapper", {
                "c-header__desktop-menu-wrapper--hidden": isAuthenticated
              })}>
                <DesktopMenu />
              </div>
            )}
            {isCityPage &&
              <a
                className="c-star__desktop-fc-link"
                href="https://www.feedbackcompany.com/nl-nl/reviews/juan-pescador/"
                rel="noopener noreferrer nofollow"
                aria-label="Feedback Company"
                target="_blank"
              >
                <Stars value={4} />
              </a>
            }
          </div>

          {/* Actions (Right column on desktop & mobile) */}
          <div className="c-header__item--right">
            <div className="c-horizontal-nav c-header__nav">
              {!hasPublishedWish && user?.accountType !== 'worker' ? <AddOffer /> : null}
              
              <div className="c-header__auth-container">
                {isAuthLoading ? (
                  <SkeletonSimple className="c-header__auth-skeleton" />
                ) : (
                  <>
                    <div className={cx("c-header__auth-group c-header__auth-group--authenticated", {
                      "c-header__auth-group--hidden": !isAuthenticated || needsFinalize
                    })}>
                      {user?.personId && hasLocation && <Conversations />}
                      {user?.personId && hasLocation && <AccountMenu />}
                    </div>
                    
                    <div className={cx("c-header__auth-group c-header__auth-group--finalize", {
                      "c-header__auth-group--hidden": !needsFinalize
                    })}>
                      <Finalize url={finalizeUrl} />
                    </div>

                    <div className={cx("c-header__auth-group c-header__auth-group--unauthenticated", {
                      "c-header__auth-group--hidden": isAuthenticated
                    })}>
                      {showSignIn && <SignIn />}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
      {isAuthenticated && user && hasLocation ? <AuthorizedMenu /> : null}
    </>
  );
};

export default Header;
